<template>
  <svg
    :id="`connection-draft`"
    class="connection"
  >
    <path
      ref="pathElement"
      d=""
    />
  </svg>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  onBeforeUnmount, onMounted, useTemplateRef,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  INodeSchemaRegistry,
} from '../types';

import {
  applyConnectionData,
} from './connection-utils';
import {
  IUserInterfaceAdapterProps,
} from './types';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
}>();

const pathElement = useTemplateRef('pathElement');

function abortDraft() {
  props.graphEditor.abortConnectionDraft();

  if (!pathElement.value) {
    return;
  }
  applyConnectionData(pathElement.value, undefined);
}

let requestAnimationFrameId: number | undefined;
function handleMouseMove(event: MouseEvent) {
  // To reduce load on the browser a limit the drag updates to exactly one update
  // per browser paint
  if (requestAnimationFrameId !== undefined) {
    cancelAnimationFrame(requestAnimationFrameId);
  }
  requestAnimationFrameId = requestAnimationFrame(() => {
    if (!pathElement.value) {
      return;
    }

    const clientMousePos = {
      x: event.clientX,
      y: event.clientY,
    };
    const worldPos = props.graphEditor.clientToWorld(clientMousePos);
    if (!worldPos) {
      applyConnectionData(pathElement.value, undefined);
      return;
    }
    applyConnectionData(pathElement.value, props.graphEditor.buildConnectionDraft(worldPos));
  });
}

function handleKeyPress(event: KeyboardEvent) {
  if (!props.graphEditor.hasActiveConnectionDraft()) {
    return;
  }
  if (event.key === 'Escape') {
    abortDraft();
  }
}

function onDraftStarted() {
  props.graphEditor.addEventListener('connection:draft:aborted', onDraftRemoved);
  props.graphEditor.addEventListener('connection:created', abortDraft);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('keyup', handleKeyPress);
}

function onDraftRemoved() {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('keyup', handleKeyPress);

  abortDraft();
}

onMounted(() => {
  props.graphEditor.addEventListener('connection:draft:started', onDraftStarted);
});

onBeforeUnmount(() => {
  props.graphEditor.removeEventListener('connection:draft:started', onDraftStarted);
  props.graphEditor.removeEventListener('connection:draft:aborted', onDraftRemoved);
  props.graphEditor.removeEventListener('connection:created', abortDraft);

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('keypress', handleKeyPress);
});
</script>

<style scoped lang="scss">
@use './styles.scss';

.connection {
  z-index: styles.$z-level-connection;
  position: absolute;
  overflow: visible !important;
  pointer-events: none;

  path {
    fill: none;
    pointer-events: auto;
    stroke: #000;
    stroke-width: 2;
    stroke-dasharray: 6 6;
  }
}
</style>
